import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  createCategory,
  updateCategory,
  type Category,
} from '@/features/inventory/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(100, 'That name is too long'),
  description: z.string().trim().max(2000, 'That description is too long'),
  parent_id: z.string(),
  sort_order: z
    .string()
    .trim()
    .refine((value) => value === '' || Number.isInteger(Number(value)), {
      message: 'Enter a whole number',
    }),
})

type FormValues = z.infer<typeof schema>

/** A category and everything under it, walked once so a category can never be offered as
 *  its own parent -- the API also refuses a cycle, but a select full of choices that always
 *  fail is worse than not offering them. */
function subtreeIds(category: Category): Set<string> {
  const ids = new Set<string>([category.id])
  for (const child of category.children ?? []) {
    for (const id of subtreeIds(child)) ids.add(id)
  }
  return ids
}

/** Flattened for the parent `<select>`, indented by depth, with a subtree excluded. */
function parentOptions(
  nodes: Category[],
  exclude: Set<string>,
  depth = 0,
): { id: string; label: string }[] {
  return nodes.flatMap((node) =>
    exclude.has(node.id)
      ? []
      : [
          { id: node.id, label: `${'— '.repeat(depth)}${node.name}` },
          ...parentOptions(node.children ?? [], exclude, depth + 1),
        ],
  )
}

/**
 * Create and edit in one dialog, same split as `TaxRuleFormDialog`. Deleting lives on the
 * list screen instead -- a destructive action with a scope this narrow (empty categories
 * only) reads better as one confirm dialog than a mode of this form.
 */
export function CategoryFormDialog({
  open,
  category,
  tree,
  onClose,
}: {
  open: boolean
  /** Absent for a new category. */
  category?: Category
  /** The whole tree, for the parent picker. */
  tree: Category[]
  onClose: () => void
}) {
  const isEdit = category !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const options = parentOptions(tree, category ? subtreeIds(category) : new Set())

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: category?.name ?? '',
      description: category?.description ?? '',
      parent_id: category?.parent_id ?? '',
      sort_order: category?.sort_order != null ? String(category.sort_order) : '0',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const shared = {
        name: values.name,
        description: values.description || null,
        parent_id: values.parent_id || null,
        sort_order: Number(values.sort_order || 0),
      }
      return category ? updateCategory(category.id, shared) : createCategory(shared)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(isEdit ? `Saved ${saved.name}.` : `Created ${saved.name}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${category.name}` : 'New category'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="category-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create category'}
            </Button>
          </>
        }
      >
        <form id="category-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Name" error={errors.name?.message}>
            {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
          </Field>

          <Field label="Description" error={errors.description?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('description')} autoComplete="off" />}
          </Field>

          <Field
            label="Parent category"
            error={errors.parent_id?.message}
            hint="Leave blank for a top-level category."
          >
            {(props) => (
              <select {...props} {...register('parent_id')} className={SELECT_CLASS}>
                <option value="">No parent</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="Sort order"
            error={errors.sort_order?.message}
            hint="Lower numbers list first among siblings."
          >
            {(props) => <Input {...props} {...register('sort_order')} inputMode="numeric" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
